import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { slackQueries } from '@/lib/queries/integrations/messaging/slack';
import { preLoadDefaultProject } from '@/lib/queries/project';

import { ChannelsTable } from './-components/ChannelsTable';
import { EmptyState } from './-components/EmptyState';
import { ErrorState } from './-components/ErrorState';

// Simple Badge component
const Badge = ({
  children,
  variant = 'default',
  className,
}: {
  children: React.ReactNode;
  variant?: 'default' | 'outline' | 'secondary';
  className?: string;
}) => (
  <span
    className={cn(
      'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
      variant === 'default' &&
        'bg-blue-100 text-blue-800 border border-blue-200',
      variant === 'outline' &&
        'bg-transparent text-gray-600 border border-gray-300',
      variant === 'secondary' &&
        'bg-gray-100 text-gray-800 border border-gray-200',
      className,
    )}
  >
    {children}
  </span>
);

export const Route = createFileRoute('/_root/integrations/messaging/slack/')({
  component: RouteComponent,
  loader: async ({ context }) => {
    const defaultProject = await preLoadDefaultProject({ context });

    context.queryClient.prefetchQuery(
      slackQueries.listChannels(defaultProject.id),
    );

    return defaultProject;
  },
});

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-pulse text-gray-500">
        <FormattedMessage id="global.loading" />
      </div>
    </div>
  );
}

function BackButton() {
  return (
    <Link to="/integrations">
      <Button variant="ghost" size="sm" className="mb-4">
        <ArrowLeft className="mr-2 h-4 w-4" />
        <FormattedMessage id="global.back" />
      </Button>
    </Link>
  );
}

function PageHeader({ connectedState }: { connectedState: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-bold">
          <FormattedMessage id="integrations.messaging.slack.title" />
        </h1>
        <p className="text-gray-600 mt-2">
          <FormattedMessage id="integrations.messaging.slack.description" />
        </p>
      </div>
      <Badge variant={connectedState ? 'default' : 'secondary'}>
        {connectedState ? (
          <FormattedMessage id="integrations.messaging.slack.status.connected" />
        ) : (
          <FormattedMessage id="integrations.messaging.slack.status.disconnected" />
        )}
      </Badge>
    </div>
  );
}

function RouteComponent() {
  const defaultProject = Route.useLoaderData();

  const {
    data: slackData,
    isLoading,
    error,
  } = useQuery(slackQueries.listChannels(defaultProject.id));

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <BackButton />
        </div>
        <ErrorState />
      </div>
    );
  }

  const channels = slackData?.channels ?? [];
  const connectedState = slackData?.connectedState ?? false;

  return (
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <BackButton />
        <PageHeader connectedState={connectedState} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <FormattedMessage id="integrations.messaging.slack.channels.title" />
          </CardTitle>
          <CardDescription>
            <FormattedMessage id="integrations.messaging.slack.channels.description" />
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState />
          ) : channels.length === 0 ? (
            <EmptyState />
          ) : (
            <ChannelsTable channels={channels} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
